// src/Signup.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import './Login.css';
import axios from 'axios';
import Cookies from 'js-cookie';

const Signup = () => {
  const [userName, setuserName] = useState('');
  const [userAddress, setuserAddress] = useState('');
  const [userPass, setuserPass] = useState('');
  const [userPass2, setuserPass2] = useState('');
  const [isPasswordVisible1, setIsPasswordVisible1] = useState(false);
  const [isPasswordVisible2, setIsPasswordVisible2] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const API_URL = import.meta.env.VITE_API_URL;
  

  // ---- OTP成功で戻ってきたら登録を続行（returnStateで判定）----
  useEffect(() => {
    const st = location.state;
    if (st?.otpVerified && st?.email) {
      if (submitting) return; // 既に処理中なら無視
      const draftRaw = sessionStorage.getItem('signupDraft');
      if (!draftRaw) {
        setErrorText('入力内容が失われました。もう一度やり直してください。');
        return;
      }
      // ← ここで即削除（2回目の発火で再実行されなくなる）
      sessionStorage.removeItem('signupDraft');
      sessionStorage.removeItem('otpFlowState');

      try {
        const draft = JSON.parse(draftRaw);
        if ((draft.userAddress || '').trim().toLowerCase() !== st.email.trim().toLowerCase()) {
          setErrorText('メールアドレスが一致しません。もう一度やり直してください。');
          return;
        }
        void finalizeSignup(draft);
      } catch {
        setErrorText('入力内容の復元に失敗しました。もう一度やり直してください。');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // ---- OTP成功後の最終登録処理 ----
  const finalizeSignup = async (draft) => {
    try {
      setSubmitting(true);
      setErrorText('');

      const name = (draft.userName || '').trim();
      const email = (draft.userAddress || '').trim();
      const pass = draft.userPass || '';
      const pass2 = draft.userPass2 || '';

      if (!name) return setErrorText('氏名を入力してください。');
      if (!email) return setErrorText('ユーザーIDを入力してください。');
      if (pass.length < 8) return setErrorText('パスワードは8文字以上にしてください。');
      if (pass !== pass2) return setErrorText('パスワード（確認）が一致しません。');

      // 分割後の作成API（/api/signup/create）に合わせる
      const res = await axios.post(
        `${API_URL}/api/signup/create`,
        { userName: name, userAddress: email, userPass: pass }
      );

      if (res.data?.success) {
        localStorage.setItem('lastLoginID', email);
        navigate('/auth/login-view/signup-thanks')
      } else {
        setErrorText(res.data?.message || '登録に失敗しました。');
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        '通信エラーが発生しました。';

      if (status === 409) {
        setErrorText('このユーザーIDは既に登録されています。');
      } else if (status === 400) {
        setErrorText(msg || '入力内容を確認してください。');
      } else if (status === 403) {
        setErrorText('CSRFエラーが発生しました。ページを再読み込みしてやり直してください。');
      } else {
        setErrorText(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ---- 「登録する」→ 事前に登録済みチェック → 未登録なら OTP 開始 → 成功後この画面で登録続行 ----
  const handleSignup = async (e) => {
    e.preventDefault();

    if (!userName.trim()) return setErrorText('氏名を入力してください。');
    if (!userAddress.trim()) return setErrorText('ユーザーIDを入力してください。');
    if (userPass.length < 8) return setErrorText('パスワードは8文字以上にしてください。');
    if (userPass !== userPass2) return setErrorText('パスワード（確認）が一致しません。');

    try {
      setSubmitting(true);
      setErrorText('');

      
      const email = userAddress.trim();

      // ① 事前に登録済みチェック（登録済みならここで止める）
      try {
        const chk = await axios.post(
          `${API_URL}/api/signup/check`,
          { userAddress: email }
        );
        if (!chk.data?.available) {
          setErrorText('このユーザーIDは既に登録されています。');
          return;
        }
      } catch (chkErr) {
        // APIは 409 を返す設計：その場合はメッセージ表示して終了、OTPは送らない
        if (chkErr?.response?.status === 409) {
          setErrorText('このユーザーIDは既に登録されています。');
          return;
        }
        const msg =
          chkErr?.response?.data?.message ??
          chkErr?.response?.data?.error ??
          '登録可否の確認に失敗しました。時間をおいて再度お試しください。';
        setErrorText(msg);
        return;
      }

      // ② 未登録なら OTP 開始
      const otpRes = await axios.post(
        `${API_URL}/api/otp/start`,
        { userAddress: email, purpose: 'signup' }
      );

      const { challengeId, ttlSec, resendCooldownSec, maskedEmail } = otpRes.data || {};
      if (!challengeId) {
        setErrorText('ワンタイムパスの開始に失敗しました。時間をおいて再度お試しください。');
        return;
      }

      // 入力一時退避
      sessionStorage.setItem(
        'signupDraft',
        JSON.stringify({
          userName: userName.trim(),
          userAddress: email,
          userPass,
          userPass2,
        })
      );

      // state消失対策 & 戻り先指定
      sessionStorage.setItem(
        'otpFlowState',
        JSON.stringify({
          challengeId,
          userAddress: email,
          maskedEmail,
          ttlSec,
          resendCooldownSec,
          returnTo: '/signup',
          returnState: { otpVerified: true, email },
        })
      );

      // ③ OTP画面へ（成功したら OtpVerify 側が /signup に戻す）
      navigate('/otpverify', {
        state: {
          challengeId,
          userAddress: email,
          maskedEmail,
          ttlSec,
          resendCooldownSec,
          returnTo: '/signup',
          returnState: { otpVerified: true, email },
        },
        replace: true,
      });
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        '通信エラーが発生しました。';

      if (status === 429) {
        setErrorText('操作が集中しています。しばらくしてからお試しください。');
      } else if (status === 400) {
        setErrorText(msg || '入力内容を確認してください。');
      } else {
        setErrorText(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card" role="dialog" aria-labelledby="signup-title">
        <h2 id="signup-title" className="login-title">新規登録</h2>
        {errorText && <div className="error-alert" role="alert">{errorText}</div>}
        <form onSubmit={handleSignup}>
          <div className="form-group">
            <label htmlFor="name">氏名</label>
            <input
              id="name"
              type="text"
              value={userName}
              onChange={(e) => setuserName(e.target.value)}
              required
              placeholder="山田 太郎"
            />
          </div>

          <div className="form-group">
            <label htmlFor="userAddress">ユーザーID（メールアドレス）</label>
            <input
              id="userAddress"
              type="email"
              value={userAddress}
              onChange={(e) => setuserAddress(e.target.value)}
              required
              placeholder="your@example.com"
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="pass">パスワード</label>
            <div className="password-field">
              <input
                id="pass"
                type={isPasswordVisible1 ? 'text' : 'password'}
                value={userPass}
                onChange={(e) => setuserPass(e.target.value)}
                required
                placeholder="8文字以上"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setIsPasswordVisible1((v) => !v)}
                aria-label={isPasswordVisible1 ? 'パスワードを非表示' : 'パスワードを表示'}
              >
                {isPasswordVisible1 ? '隠す' : '表示'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="pass2">パスワード（確認）</label>
            <div className="password-field">
              <input
                id="pass2"
                type={isPasswordVisible2 ? 'text' : 'password'}
                value={userPass2}
                onChange={(e) => setuserPass2(e.target.value)}
                required
                placeholder="もう一度入力"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setIsPasswordVisible2((v) => !v)}
                aria-label={isPasswordVisible2 ? 'パスワードを非表示' : 'パスワードを表示'}
              >
                {isPasswordVisible2 ? '隠す' : '表示'}
              </button>
            </div>
          </div>

          <button type="submit" className="login-button" disabled={submitting}>
            {submitting ? '登録中…' : '登録する'}
          </button>
        </form>

        <div style={{ marginTop: 10, textAlign: 'center', fontSize: 13 }}>
          すでにアカウントをお持ちの方は <Link to="/login">ログイン</Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;
